<!-- CartPayloadTester.vue - Test component to view cart payload properties -->
<template>
  <div class="cart-payload-tester">
    <div class="tester-header">
      <h2>🧪 Cart Payload Tester</h2>
      <p>Test transform methods and view generated properties</p>
    </div>

    <!-- Method Selection -->
    <div class="method-selector">
      <label>Select Artwork Method:</label>
      <select v-model="selectedMethod" @change="updatePayload">
        <option value="new">Start New Imprint</option>
        <option value="send">Send Us Your Artwork</option>
        <option value="previous">Use My Previous Artwork</option>
      </select>
    </div>

    <!-- Generate Button -->
    <div class="action-buttons">
      <button @click="generatePayload" class="btn-generate">
        Generate Payload
      </button>
      <button @click="copyToClipboard" class="btn-copy" :disabled="!currentPayload">
        📋 Copy JSON
      </button>
    </div>

    <!-- Payload Display -->
    <div v-if="currentPayload" class="payload-display">
      <!-- Imprint Lines -->
      <div class="section">
        <h3>📝 imprint_lines</h3>
        <pre>{{ JSON.stringify(currentPayload.imprint_lines, null, 2) }}</pre>
      </div>

      <!-- Properties -->
      <div class="section">
        <h3>🏷️ properties</h3>
        <div class="properties-table">
          <table>
            <thead>
              <tr>
                <th>Property Name</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, key) in currentPayload.properties" :key="key">
                <td class="property-key">{{ key }}</td>
                <td class="property-value">
                  <span v-if="typeof value === 'object'">
                    <pre>{{ JSON.stringify(value, null, 2) }}</pre>
                  </span>
                  <span v-else>{{ value }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Complete JSON -->
      <div class="section">
        <h3>📦 Complete Payload JSON</h3>
        <pre class="json-display">{{ JSON.stringify(currentPayload, null, 2) }}</pre>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="error-message">
      <strong>Error:</strong> {{ error }}
    </div>

    <!-- Stats -->
    <div v-if="stats" class="stats">
      <h3>📊 Stats</h3>
      <ul>
        <li><strong>Method:</strong> {{ stats.method }}</li>
        <li><strong>Unique Colors:</strong> {{ stats.uniqueColors }}</li>
        <li><strong>Has Custom Color:</strong> {{ stats.hasCustomColor ? 'Yes' : 'No' }}</li>
        <li><strong>Setup Fee:</strong> ${{ stats.setupFee }}</li>
        <li><strong>Running Fee:</strong> ${{ stats.runningFee }}</li>
        <li><strong>Custom Color Fee:</strong> ${{ stats.customColorFee }}</li>
        <li><strong>Total Fees:</strong> ${{ stats.totalFees }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { useNewImprintStore } from '../../stores/useNewImprintStore';
import { useImprintStore } from '../../stores/useImprintStore';

export default {
  name: 'CartPayloadTester',

  data() {
    return {
      selectedMethod: 'new',
      currentPayload: null,
      error: null,
      stats: null
    };
  },

  setup() {
    const newImprintStore = useNewImprintStore();
    const imprintStore = useImprintStore();
    
    return {
      newImprintStore,
      imprintStore
    };
  },

  methods: {
    /**
     * Generate payload based on selected method
     */
    generatePayload() {
      this.error = null;
      this.currentPayload = null;
      this.stats = null;
      
      try {
        let payload;
        
        if (this.selectedMethod === 'new') {
          payload = this.newImprintStore.transformToCartPayload();
          
          if (payload) {
            const imprint = this.newImprintStore.currentImprint;
            this.stats = {
              method: 'Start New Imprint',
              uniqueColors: imprint.maxColorsTracking.uniqueColors.length,
              hasCustomColor: imprint.textLines.some(l => l.customColor),
              setupFee: payload.properties._price_details.setup_fee,
              runningFee: payload.properties._price_details.running_fee,
              customColorFee: payload.properties._price_details.customColourFee,
              totalFees: payload.properties._price_details.setup_fee +
                         payload.properties._price_details.running_fee +
                         payload.properties._price_details.customColourFee +
                         payload.properties._price_details.proof_fee
            };
          }
        } else if (this.selectedMethod === 'send') {
          // Get from parent component if available
          const parent = this.getParentComponent();
          if (parent && typeof parent.transformSendArtworkToCart === 'function') {
            payload = parent.transformSendArtworkToCart();
          } else {
            // Create mock payload for testing
            payload = this.createMockSendArtworkPayload();
          }
          
          if (payload) {
            this.stats = {
              method: 'Send Us Your Artwork',
              uniqueColors: 0,
              hasCustomColor: false,
              setupFee: payload.properties._price_details.setup_fee,
              runningFee: payload.properties._price_details.running_fee,
              customColorFee: payload.properties._price_details.customColourFee,
              totalFees: payload.properties._price_details.setup_fee +
                         payload.properties._price_details.proof_fee
            };
          }
        } else if (this.selectedMethod === 'previous') {
          // Get from parent component if available
          const parent = this.getParentComponent();
          if (parent && typeof parent.transformPreviousArtworkToCart === 'function') {
            payload = parent.transformPreviousArtworkToCart();
          } else {
            // Create mock payload for testing
            payload = this.createMockPreviousArtworkPayload();
          }
          
          if (payload) {
            this.stats = {
              method: 'Use My Previous Artwork',
              uniqueColors: 0,
              hasCustomColor: false,
              setupFee: payload.properties._price_details.setup_fee,
              runningFee: payload.properties._price_details.running_fee,
              customColorFee: payload.properties._price_details.customColourFee,
              totalFees: payload.properties._price_details.setup_fee +
                         payload.properties._price_details.proof_fee
            };
          }
        }
        
        if (!payload) {
          this.error = 'No payload generated. Make sure to fill in required fields first.';
          return;
        }
        
        this.currentPayload = payload;
        console.log('[CartPayloadTester] Generated payload:', payload);
        
      } catch (err) {
        this.error = err.message;
        console.error('[CartPayloadTester] Error:', err);
      }
    },

    /**
     * Get parent component (CustomizeProductSection)
     */
    getParentComponent() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options.name === 'CustomizeProductSection') {
          return parent;
        }
        parent = parent.$parent;
      }
      return null;
    },

    /**
     * Create mock Send Artwork payload for testing
     */
    createMockSendArtworkPayload() {
      return {
        imprint_lines: {},
        properties: {
          "Imprint location": "FULL FRONT, 8″W X 13″H",
          "_Imprint Location SKU": "IMP_LOC_FF",
          "Imprint Method": "Screenprint",
          "_Imprint Method SKU": "G640_SCRE",
          "Special Instruction": "Send Us Your Artwork After Ordering. \r\n\r\nComment: Test comment",
          "_price_details": {
            setup_fee: 30,
            proof_fee: 5,
            image_fee: 0,
            customColourFee: 0,
            running_fee: 0
          }
        }
      };
    },

    /**
     * Create mock Previous Artwork payload for testing
     */
    createMockPreviousArtworkPayload() {
      return {
        imprint_lines: {},
        properties: {
          "Imprint location": "LEFT SLEEVE, 3.5″W X 3.5″H",
          "_Imprint Location SKU": "IMP_LOC_LS",
          "Imprint Method": "Embroidered",
          "_Imprint Method SKU": "G640_EMBR",
          "Special Instruction": "Use My Previous Artwork.\r\n\r\nComment: Test comment",
          "_Previous Artwork ID": "ARTWORK_123",
          "_Reference Order": "#ORD12345",
          "_price_details": {
            setup_fee: 0,
            proof_fee: 0,
            image_fee: 0,
            customColourFee: 0,
            running_fee: 0
          }
        }
      };
    },

    /**
     * Update payload when method changes
     */
    updatePayload() {
      this.currentPayload = null;
      this.error = null;
      this.stats = null;
    },

    /**
     * Copy payload to clipboard
     */
    async copyToClipboard() {
      if (!this.currentPayload) return;
      
      try {
        const text = JSON.stringify(this.currentPayload, null, 2);
        await navigator.clipboard.writeText(text);
        alert('Payload copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
        alert('Failed to copy to clipboard');
      }
    }
  },

  mounted() {
    console.log('[CartPayloadTester] Mounted');
    console.log('[CartPayloadTester] Available stores:', {
      newImprintStore: !!this.newImprintStore,
      imprintStore: !!this.imprintStore
    });
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/utils' as *;
.cart-payload-tester {
  width: 100%;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
  margin: 20px 0;
}

.tester-header {
  margin-bottom: 20px;
  
  h2 {
    font-family: $font-family-base;
    font-size: 24px;
    margin: 0 0 8px 0;
    color: #252525;
  }
  
  p {
    font-family: $font-family-base;
    font-size: 14px;
    color: #666;
    margin: 0;
  }
}

.method-selector {
  margin-bottom: 16px;
  
  label {
    display: block;
    font-family: $font-family-base;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #252525;
  }
  
  select {
    width: 100%;
    max-width: 300px;
    padding: 10px;
    font-size: 14px;
    font-family: $font-family-base;
    border: 1px solid #D9D9D9;
    border-radius: 5px;
    background: white;
    
    &:focus {
      outline: none;
      border-color: #0D72B1;
    }
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  
  button {
    padding: 10px 20px;
    font-family: $font-family-base;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.2s;
    
    &.btn-generate {
      background: #0D72B1;
      color: white;
      
      &:hover {
        background: #0a5d93;
      }
    }
    
    &.btn-copy {
      background: #28a745;
      color: white;
      
      &:hover {
        background: #218838;
      }
      
      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
  }
}

.payload-display {
  .section {
    background: white;
    border: 1px solid #D9D9D9;
    border-radius: 5px;
    padding: 16px;
    margin-bottom: 16px;
    
    h3 {
      font-family: $font-family-base;
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 12px 0;
      color: #252525;
    }
    
    pre {
      background: #f8f9fa;
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.5;
      margin: 0;
    }
  }
}

.properties-table {
  overflow-x: auto;
  
  table {
    width: 100%;
    border-collapse: collapse;
    font-family: $font-family-base;
    font-size: 13px;
    
    thead {
      background: #f8f9fa;
      
      th {
        text-align: left;
        padding: 10px;
        font-weight: 600;
        border-bottom: 2px solid #D9D9D9;
      }
    }
    
    tbody {
      tr {
        border-bottom: 1px solid #e9ecef;
        
        &:hover {
          background: #f8f9fa;
        }
      }
      
      td {
        padding: 10px;
        vertical-align: top;
        
        &.property-key {
          font-weight: 600;
          color: #0D72B1;
          width: 40%;
        }
        
        &.property-value {
          color: #252525;
          
          pre {
            background: #f8f9fa;
            padding: 8px;
            border-radius: 3px;
            margin: 0;
            font-size: 11px;
          }
        }
      }
    }
  }
}

.json-display {
  max-height: 400px;
  overflow-y: auto;
}

.error-message {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 12px;
  border-radius: 5px;
  margin-bottom: 16px;
  font-family: $font-family-base;
  font-size: 14px;
}

.stats {
  background: white;
  border: 1px solid #D9D9D9;
  border-radius: 5px;
  padding: 16px;
  
  h3 {
    font-family: $font-family-base;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px 0;
    color: #252525;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      font-family: $font-family-base;
      font-size: 14px;
      padding: 6px 0;
      border-bottom: 1px solid #e9ecef;
      
      &:last-child {
        border-bottom: none;
      }
      
      strong {
        color: #0D72B1;
        margin-right: 8px;
      }
    }
  }
}
</style>
